import { ActionItemId } from "@inrev/common";
import { ActionItem } from "@portal/api/action-item/admin";
import {
	createContext,
	ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from "react";
import { useSearchParams } from "react-router-dom";

type ActionItemsSelectionProviderProps = {
	children: ReactNode;
};

export type ActionItemsSelectionContext = {
	selectedActionItemId?: string;
	editActionItem?: ActionItem;
};

export type ActionItemsSelectionDispatchContext = {
	setSelectedActionItemId: (id?: string) => void;
	setEditActionItem: (actionItem?: ActionItem) => void;
};

const defaultActionItemsContext = {
	selectedActionItemId: undefined,
	editActionItem: undefined,
};

const defaultActionItemsDispatchContext: ActionItemsSelectionDispatchContext = {
	setSelectedActionItemId: () => {},
	setEditActionItem: () => {},
};

const ActionItemsSelectionContext =
	createContext<ActionItemsSelectionContext>(defaultActionItemsContext);

const ActionItemsSelectionDispatchContext = createContext<ActionItemsSelectionDispatchContext>(
	defaultActionItemsDispatchContext,
);

export const ActionItemsSelectionProvider = ({ children }: ActionItemsSelectionProviderProps) => {
	const [selectedActionItemId, setSelectedActionItemIdState] = useState<string | undefined>(
		undefined,
	);
	const [editActionItem, setEditActionItem] = useState<ActionItem | undefined>(undefined);
	const [searchParams, setSearchParams] = useSearchParams();

	const actionItemIdParam = useMemo(
		() => (searchParams.get("actionItemId") as ActionItemId) || null,
		[searchParams],
	);

	useEffect(() => {
		setSelectedActionItemIdState(actionItemIdParam || undefined);
	}, [actionItemIdParam]);

	const setSelectedActionItemId = useCallback(
		(actionItemId?: string) => {
			setSelectedActionItemIdState(actionItemId);
			setSearchParams((prev) => {
				const next = new URLSearchParams(prev);
				if (actionItemId === undefined) {
					next.delete("actionItemId");
				} else {
					next.set("actionItemId", actionItemId);
				}
				return next;
			});
		},
		[setSearchParams],
	);

	const actionItemsContextValue = useMemo(
		() => ({
			selectedActionItemId,
			editActionItem,
		}),
		[selectedActionItemId, editActionItem],
	);

	const actionItemsDispatchContextValue = useMemo(
		() => ({
			setSelectedActionItemId,
			setEditActionItem,
		}),
		[setSelectedActionItemId, setEditActionItem],
	);

	return (
		<ActionItemsSelectionContext.Provider value={actionItemsContextValue}>
			<ActionItemsSelectionDispatchContext.Provider value={actionItemsDispatchContextValue}>
				{children}
			</ActionItemsSelectionDispatchContext.Provider>
		</ActionItemsSelectionContext.Provider>
	);
};

export const useActionItemsSelection = () => {
	return useContext(ActionItemsSelectionContext);
};

export const useActionItemsSelectionDispatch = () => {
	return useContext(ActionItemsSelectionDispatchContext);
};
